import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { VideoAPIInterrupt } from 'tag/ts/tag/api/video/VideoAPIInterrupt';
import { VideoAPIUtilities } from 'tag/ts/tag/api/video/VideoAPIUtilities';
import { VideoLoadManager } from 'tag/ts/tag/api/video/VideoLoadManager';
import { IVideoAPI } from 'tag/ts/tag/interfaces/api/IVideoAPI';
import { IVideoAPIInterrupt } from 'tag/ts/tag/interfaces/api/IVideoAPIInterrupt';
import { IVideoAPIUtilities } from 'tag/ts/tag/interfaces/api/IVideoAPIUtilities';
import { IVideoLoadManager } from 'tag/ts/tag/interfaces/api/IVideoLoadManager';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IVideoApGetCurrentTime } from 'tag/ts/tag/interfaces/api/IVideoApGetCurrentTime';
import { IVideoApiIn } from 'tag/ts/tag/interfaces/api/IVideoApiIn';
import { IVideoApiInterruptArgument } from 'tag/ts/tag/interfaces/arguments/IVideoApiInterruptArgument';
import { ILoadRequest } from 'tag/ts/tag/interfaces/model/ILoadRequest';
import { ICallbacksVO } from 'tag/ts/tag/interfaces/callbacks/ICallbacksVO';
import { ITagOnceSettings } from 'tag/ts/tag/interfaces/model/ITagOnceSettings';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Video API — thin public API shell.
 *
 * Delegates load() lifecycle (throttling, concurrency, pending slot, provenance tracing)
 * to VideoLoadManager. Delegates utilities (stopPlayer, param whitelist, etc.) to
 * VideoAPIUtilities. Delegates interrupt handling to VideoAPIInterrupt.
 *
 * Same pattern as AviaAdapter → AviaLifecycleManager.
 */
export class VideoAPI extends AbstractTagClass implements IVideoAPI {

	#videoAPIUtilities: IVideoAPIUtilities;
	#VideoAPIInterrupt: IVideoAPIInterrupt;
	#loadManager: IVideoLoadManager;

	constructor( argument: ITagAbstractClassArgument ) {
		super( { ...argument, name: 'VideoAPI' } );

		this.#videoAPIUtilities = new VideoAPIUtilities( { tag: this.tag } );
		this.#loadManager = new VideoLoadManager( { tag: this.tag, videoAPIUtilities: this.#videoAPIUtilities } );
		this.#VideoAPIInterrupt = new VideoAPIInterrupt( { tag: this.tag } );
	}

	/**
	 * @description Returns the load request provenance history for this session.
	 * Accessible at `window.SmartTag.list[0].snapshot.loadHistory`.
	 */
	get loadHistory(): ILoadRequest[] {
		return this.#loadManager.loadHistory;
	}

	get IN(): IVideoApiIn {
		return {
			/**
			 * @description Requests to load a video with the provided parameters.
			 * Delegates to VideoLoadManager which owns throttling, concurrency, and tracing.
			 *
			 * @param {IParamsVO} incomingParamsVO - The parameters for the video to load.
			 * @param {ICallbacksVO} callbacksVO - Callback functions to be merged and used during the load process.
			 * @param {ITagOnceSettings} onceSettings - Settings for single execution of the load operation.
			 * @returns {Promise<IPromiseResolveGeneric>} A promise that resolves when the video load process completes.
			 */
			load: ( incomingParamsVO: IParamsVO = {} as IParamsVO, callbacksVO: ICallbacksVO = {} as ICallbacksVO, onceSettings: ITagOnceSettings = {} ): Promise<IPromiseResolveGeneric> => {
				return this.#loadManager.load( incomingParamsVO, callbacksVO, onceSettings );
			},

			/**
			 * @description Returns current content playback position and stream time i.e. for Chromecast.
			 * @returns {IVideoApGetCurrentTime}
			 */
			getCurrentTime: (): IVideoApGetCurrentTime => {
				return this.tag?.controller?.playerManager?.currentTime;
			},

			/**
			 * @description Pauses playback upon incoming interrupt request and executes callback passed to Smart Tag
			 * @param {IVideoApiInterruptArgument} argument
			 */
			interrupt: ( argument: IVideoApiInterruptArgument ): void => {
				this.#VideoAPIInterrupt?.interrupt( argument );
			},

			/**
			 * @description Returns status of video player interrupt from VideoAPIInterrupt instance
			 * @returns {boolean}
			 */
			interrupted: (): boolean => {
				return this.#VideoAPIInterrupt?.interrupted;
			}
		};
	}

}